"use client";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { faChevronLeft, faChevronRight, FontAwesomeIcon } from "@/constants/icons";

interface Props {
    children: React.ReactNode[];
}

/* Cards visible per viewport size */
const DESKTOP_VISIBLE = 5;
const TABLET_VISIBLE  = 3;
const MOBILE_VISIBLE  = 2;

function useVisibleCount() {
    const [count, setCount] = useState(DESKTOP_VISIBLE);

    useEffect(() => {
        const update = () => {
            if (window.innerWidth < 640)       setCount(MOBILE_VISIBLE);
            else if (window.innerWidth < 1024) setCount(TABLET_VISIBLE);
            else                               setCount(DESKTOP_VISIBLE);
        };
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    return count;
}

export default function MerchantSlider({ children }: Props) {
    const total   = children.length;
    const visible = useVisibleCount();
    const GAP     = 16; // px — matches gap-4

    /* Clamp idx so we never go past the last full page */
    const maxIdx  = Math.max(0, total - visible);
    const [idx, setIdx] = useState(0);

    /* Re-clamp if screen resizes and current idx is now out of range */
    useEffect(() => {
        setIdx((i) => Math.min(i, Math.max(0, total - visible)));
    }, [visible, total]);

    const canPrev = idx > 0;
    const canNext = idx < maxIdx;

    const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);
    const next = useCallback(() => setIdx((i) => Math.min(maxIdx, i + 1)), [maxIdx]);

    /* ── Static grid when everything fits ── */
    if (total <= visible) {
        return (
            <div className={`grid gap-4 ${
                visible === MOBILE_VISIBLE  ? "grid-cols-2" :
                visible === TABLET_VISIBLE  ? "grid-cols-3" :
                                              "grid-cols-5"
            } justify-items-center`}>
                {children}
            </div>
        );
    }

    /* ── Translate formula ──────────────────────────────────────────
       Each card's width  = (containerWidth - GAP*(visible-1)) / visible
       Step per card      = cardWidth + GAP
       We add padding-y on the outer wrapper and remove overflow-hidden
       so hover lift + shadow are never clipped.
    ────────────────────────────────────────────────────────────────── */
    const cardWidthExpr  = `(100% - ${GAP * (visible - 1)}px) / ${visible}`;
    const stepExpr       = `(${cardWidthExpr}) + ${GAP}px`;
    const translateExpr  = `calc(-1 * ${idx} * (${stepExpr}))`;

    return (
        /* py-3 gives room for hover lift + shadow without clipping */
        <div className="relative py-3">

            {/* Left button */}
            <button
                onClick={prev}
                disabled={!canPrev}
                aria-label="Previous"
                className={`absolute -left-4 top-1/2 -translate-y-1/2 z-10
                            w-9 h-9 rounded-full bg-white border border-slate-200
                            shadow-md flex items-center justify-center transition-all duration-200
                            ${canPrev
                                ? "hover:bg-[#D61C4E] hover:border-[#D61C4E] hover:text-white text-slate-600 cursor-pointer"
                                : "opacity-30 cursor-not-allowed text-slate-300"}`}
            >
                <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
            </button>

            {/* ── Viewport — no overflow-hidden so shadows/lift show freely ── */}
            <div className="overflow-visible w-full">
                {/*
                    The track is wider than the viewport on purpose.
                    We clip it with a negative-margin trick on the container
                    so it doesn't cause horizontal scroll on the page.
                */}
                <div
                    className="flex gap-4 transition-transform duration-400 ease-in-out"
                    style={{ transform: `translateX(${translateExpr})` }}
                >
                    {children.map((child, i) => (
                        <div
                            key={i}
                            className="shrink-0"
                            style={{ width: `calc(${cardWidthExpr})` }}
                        >
                            {child}
                        </div>
                    ))}
                </div>
            </div>

            {/* Right button */}
            <button
                onClick={next}
                disabled={!canNext}
                aria-label="Next"
                className={`absolute -right-4 top-1/2 -translate-y-1/2 z-10
                            w-9 h-9 rounded-full bg-white border border-slate-200
                            shadow-md flex items-center justify-center transition-all duration-200
                            ${canNext
                                ? "hover:bg-[#D61C4E] hover:border-[#D61C4E] hover:text-white text-slate-600 cursor-pointer"
                                : "opacity-30 cursor-not-allowed text-slate-300"}`}
            >
                <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            </button>

            {/* ── Dot indicators (mobile only) ── */}
            <div className="flex sm:hidden justify-center gap-1.5 mt-4">
                {Array.from({ length: maxIdx + 1 }).map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setIdx(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        className={`h-1.5 rounded-full transition-all duration-300
                            ${i === idx
                                ? "w-5 bg-[#D61C4E]"
                                : "w-1.5 bg-slate-300 hover:bg-slate-400"}`}
                    />
                ))}
            </div>
        </div>
    );
}
